/* eslint-disable react-hooks/set-state-in-effect */
import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Check, Lock, GraduationCap } from "lucide-react";

export default function GradePage() {
  const [data, setData] = useState(null);
  const { user, refresh: refreshAuth } = useAuth();

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/grade");
      setData(data);
      // Keep the top-bar badge in sync : if the server-computed grade drifts
      // from what AuthContext knows, refresh /auth/me immediately.
      if (data?.current?.id && user?.grade?.id !== data.current.id) {
        refreshAuth();
      }
    } catch { /* ignore */ }
  }, [user, refreshAuth]);

  useEffect(() => { load(); const t = setInterval(load, 5000); return () => clearInterval(t); }, [load]);

  if (!data) return <div className="font-mono text-xs text-[#7A7A7A]">Chargement…</div>;

  const { grades, current, next, stats } = data;

  return (
    <div data-testid="grade-page">
      <div className="flex justify-between items-end mb-4 flex-wrap gap-3">
        <div>
          <div className="overline">Renommée académique</div>
          <h1 className="font-serif-display text-4xl md:text-5xl leading-none mt-1">Grade</h1>
        </div>
        <div className="flex items-center gap-2 font-mono text-[11px] text-[#4A4A4A]">
          <GraduationCap size={14}/>
          Grade actuel : <strong className="text-[#1A1A1A]">{current.title}</strong>
        </div>
      </div>

      {/* Progress toward next */}
      {next && (
        <div className="panel-strong p-4 mb-6" data-testid="grade-next-panel">
          <div className="flex items-center gap-4 flex-wrap">
            <img
              src={`/img/lore/${next.illustration}`}
              alt={next.title}
              className="w-24 h-24 object-cover border border-[#2B2B2B] shrink-0"
              onError={(e) => { e.target.style.display = "none"; }}
            />
            <div className="flex-1 min-w-[220px]">
              <div className="overline text-[10px] text-[#B87333]">Prochain palier</div>
              <h2 className="font-serif-display text-2xl md:text-3xl leading-none mt-0.5">{next.title}</h2>
              <p className="text-xs text-[#4A4A4A] mt-1 italic">{next.description}</p>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-4" data-testid="grade-requirements">
            <ReqRow label="Badges cumulés" value={stats.badges_total} target={next.req_badges_total}/>
            <ReqRow label="Espèces vues" value={stats.seen} target={next.req_seen}/>
            <ReqRow label="Espèces capturées" value={stats.caught} target={next.req_caught}/>
            <ReqRow label="Dresseurs actifs" value={stats.active_trainers} target={next.req_active_trainers}/>
            <ReqRow label="Ligues remportées" value={stats.league_wins} target={next.req_league_wins}/>
            {next.req_pokedex_complete && (
              <ReqRow label="Pokédex complet" value={stats.pokedex_complete ? 1 : 0} target={1}/>
            )}
          </div>
        </div>
      )}

      {/* All grades table */}
      <div className="grid gap-3" data-testid="grade-list">
        {grades.map((g) => {
          const isCurrent = g.id === current.id;
          const isPast = g.id < current.id;
          const isFuture = g.id > current.id;
          return (
            <div
              key={g.id}
              className={`p-4 flex gap-4 flex-wrap items-start ${
                isCurrent ? "diploma" :
                isPast ? "panel opacity-70" : "panel"
              }`}
              data-testid={`grade-row-${g.id}`}
            >
              <img
                src={`/img/lore/${g.illustration}`}
                alt={g.title}
                className={`w-24 h-24 object-cover border border-[#2B2B2B] shrink-0 ${isFuture ? "grayscale" : ""}`}
                onError={(e) => { e.target.style.display = "none"; }}
              />
              <div className="flex-1 min-w-[240px]">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`font-mono text-[10px] px-1.5 py-0.5 ${isCurrent ? "bg-[#2E4F32] text-[#F4EFE6]" : "border border-[#4A4A4A] text-[#4A4A4A]"}`}>
                    Grade {g.id}
                  </span>
                  {isCurrent && <Check size={12} className="text-[#2E4F32]"/>}
                  {isFuture && <Lock size={12} className="text-[#7A7A7A]"/>}
                </div>
                <h3 className="font-serif-display text-xl md:text-2xl leading-none mt-1">{g.title}</h3>
                <p className="text-xs text-[#4A4A4A] mt-1 italic">{g.description}</p>
                <div className="grid grid-cols-3 gap-2 mt-3">
                  <MiniStat label="Capacité PC" value={g.pc_capacity} color="#2E4F32"/>
                  <MiniStat label="Réduc. boutique" value={`${(g.shop_discount * 100).toFixed(0)}%`} color="#B87333"/>
                  <MiniStat label="XP dresseur" value={`+${(g.trainer_xp_bonus * 100).toFixed(0)}%`} color="#2E4F32"/>
                </div>
              </div>
              {isCurrent && (
                <>
                  <div className="diploma-ribbon" aria-hidden="true"/>
                  <div className="diploma-seal" aria-hidden="true">
                    Prof.<br/>Aeter
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ReqRow({ label, value, target }) {
  if (target === 0) return null;
  const pct = Math.min(100, Math.round((value / target) * 100));
  const done = value >= target;
  return (
    <div className="panel p-2" data-testid={`req-${label}`}>
      <div className="flex justify-between text-[10px] font-mono">
        <span className="text-[#4A4A4A]">{label}</span>
        <span className={done ? "text-[#2E4F32]" : "text-[#1A1A1A]"}>{value}/{target}{done && " ✓"}</span>
      </div>
      <div className="h-[4px] bg-[#EAE4D9] border border-[#2B2B2B] mt-1">
        <div className="h-full" style={{ width: `${pct}%`, backgroundColor: done ? "#2E4F32" : "#B87333" }}/>
      </div>
    </div>
  );
}

function MiniStat({ label, value, color }) {
  return (
    <div className="text-center">
      <div className="overline text-[9px]">{label}</div>
      <div className="font-serif-display text-xl leading-none mt-0.5" style={{ color }}>{value}</div>
    </div>
  );
}
